import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { usePostHog } from "@posthog/react";
import { MadeBy } from "../components/made-by";
import logo from "../assets/copresent-logo.svg";
import { getStoredHostSession } from "../webAuth";
import { DEFAULT_INTERVAL, parseInterval, PRICES } from "../config/plans";
import { readStoredTheme, themeTokens } from "../theme";
import { UPGRADED_PARAM, takeUpgradeReturn } from "../upgradeReturn";
import { CHECKOUT_COMPLETED } from "../analytics/events";

// Themed from the SAME stored preference as the controller. The buyer reaches
// this page seconds after leaving a dark controller for Stripe; a hardcoded
// white page here was a flashbang at the one moment we most want to look
// trustworthy. Read once at mount — there's no toggle on this page, and a
// receipt is not where someone changes their theme.
const Page = styled.div<{ $dark: boolean }>`
  ${(p) => themeTokens(p.$dark)}

  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 16px 48px;
  box-sizing: border-box;
  background-color: var(--bg-page);
  color: var(--text-primary);
`;

const Logo = styled.img`
  width: 48px;
  height: 48px;
  margin-top: 44px;
`;

// Two bands, matching the upgrade dialog the buyer just came from: a tinted
// confirmation zone, then the plain "what now" zone. The old card gave the
// badge, heading, two paragraphs and the button one flat 14px gap each, so
// nothing led — the eye had no idea which line was the answer to "did it work?".
const Card = styled.div`
  width: 100%;
  max-width: 440px;
  margin-top: 24px;
  box-sizing: border-box;
  background: var(--bg-card);
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 1px 2px var(--shadow-near),
    0 24px 60px -16px var(--shadow-far);
`;

const Confirmation = styled.div`
  padding: 30px 26px 24px;
  text-align: center;
  border-bottom: 1px solid var(--border-soft);
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(34, 197, 94, 0.16);
  color: #22c55e;
  /* Concentric ring instead of a bigger disc — presence without shouting. */
  box-shadow: 0 0 0 8px rgba(34, 197, 94, 0.06);
`;

const Heading = styled.h1`
  margin: 16px 0 0;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--text-headline);
`;

// The receipt line: what they bought, for how much. Quiet, but it's the detail
// that makes the page read as a receipt rather than a generic "thanks".
const Receipt = styled.p`
  margin: 8px 0 0;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-muted);
`;

const Body = styled.div`
  padding: 22px 26px 24px;
`;

const SectionLabel = styled.h2`
  margin: 0 0 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--text-fade);
`;

// Left-aligned on purpose. The old copy centred a four-line paragraph, which
// gives every line a different starting edge and makes real sentences work to
// read; centring earns its place on the one-line heading above, not here.
const Text = styled.p`
  margin: 0;
  font-size: 14.5px;
  line-height: 1.55;
  color: var(--text-secondary);
`;

const Email = styled.strong`
  color: var(--text-primary);
  font-weight: 600;
  /* A long Google address must not widen the card on a phone. */
  overflow-wrap: anywhere;
`;

const HomeButton = styled.a`
  display: block;
  margin-top: 22px;
  width: 100%;
  box-sizing: border-box;
  text-align: center;
  text-decoration: none;
  border-radius: 11px;
  padding: 14px 16px;
  font-size: 15.5px;
  font-weight: 700;
  color: #fff;
  background: var(--link);
  box-shadow: 0 6px 18px -6px rgba(99, 102, 241, 0.65);
  transition: background-color 0.12s, box-shadow 0.12s, transform 0.04s;

  &:hover {
    background: var(--link-hover);
  }
  &:active {
    transform: translateY(1px);
  }
`;

const Help = styled.p`
  margin: 14px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--text-fade);
  text-align: center;
`;

// Same treatment as the controller's footer. Without the `a` rule the Copresent
// link falls back to the browser's default blue, which was invisible while this
// page was light-only and glares once it can be dark. (Privacy Policy carries an
// inline colour inside MadeBy, so it wins over this either way — same as on the
// controller.)
const Footer = styled(MadeBy)`
  margin-top: 28px;
  font-size: 12.5px;
  text-align: center;
  color: var(--text-muted);

  a {
    color: var(--link);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      color: var(--link-hover);
      text-decoration: underline;
    }
  }
`;

const CheckIcon = () => (
  <svg width="26" height="26" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M4 10.5l4 4 8-9"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Where Stripe sends the buyer after a successful payment — the success_url the
// account service sets on the Checkout Session, with the interval they chose.
//
// Normally a WAYPOINT, not a destination: the controller stashes its path
// before handing off to Stripe, and this page bounces straight back to it with
// ?upgraded=1 so the buyer lands where they left, and the confirmation shows up
// as a dialog over their own presentation. The card below is the fallback for
// when there's no stored path — storage disabled, a cleared browser, or someone
// opening this URL cold — because Stripe's redirect is a single static URL and
// always lands here regardless.
//
// Either way this is a receipt, NOT proof of entitlement: it renders purely
// because Stripe redirected here, and it never checks anything. The plan itself
// is granted server-side from the payment record — so the copy must not promise
// that Pro is already live, or a host will go straight back to a full room and
// think the purchase failed.
const UpgradeSuccess = () => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const session = getStoredHostSession();
  const [dark] = useState(() => readStoredTheme() === "dark");
  // Memoised inside takeUpgradeReturn, so StrictMode's double-invoke of this
  // initializer still yields the same path rather than clearing it out from
  // under itself.
  const [returnTo] = useState(takeUpgradeReturn);
  const [interval] = useState(
    () => parseInterval(new URLSearchParams(window.location.search).get("interval")) ?? DEFAULT_INTERVAL,
  );

  useEffect(() => {
    posthog.capture(CHECKOUT_COMPLETED, {
      plan: interval,
      // Distinguishes the bounce from the fallback, so the fallback silently
      // becoming the common path would be visible rather than invisible.
      returned_to_controller: Boolean(returnTo),
    });
  }, [posthog, returnTo, interval]);

  useEffect(() => {
    if (!returnTo) return;
    const separator = returnTo.includes("?") ? "&" : "?";
    // In-app navigation, not window.location: it keeps the SPA (and the capture
    // above) alive, and replace() keeps this spent receipt out of history so
    // Back doesn't land the buyer on it again.
    navigate(`${returnTo}${separator}${UPGRADED_PARAM}=1`, { replace: true });
  }, [returnTo, navigate]);

  // Bouncing: paint the themed background only. Rendering the receipt for the
  // one frame before the effect runs would flash a card they'd never read.
  if (returnTo) return <Page $dark={dark} />;

  return (
    <Page $dark={dark}>
      <Logo src={logo} alt="Copresent" />
      <Card>
        <Confirmation>
          <Badge>
            <CheckIcon />
          </Badge>
          <Heading>Payment received</Heading>
          <Receipt>
            Copresent Pro · {PRICES[interval].amount} {PRICES[interval].per} ·{" "}
            {PRICES[interval].label.toLowerCase()} subscription
          </Receipt>
        </Confirmation>

        <Body>
          <SectionLabel>What happens next</SectionLabel>
          <Text>
            We're switching Pro on for{" "}
            <Email>{session?.profile?.email || "your account"}</Email>. It
            usually takes a few seconds — after that, your presentations take
            unlimited copresenters.
          </Text>
          <HomeButton href="/">Back to Copresent</HomeButton>
          <Help>
            Still capped a few minutes from now? Reload the host controller. If
            it sticks, reply to your Stripe receipt and we'll sort it out. You
            can change plan or cancel any time from Billing on the upgrade page.
          </Help>
        </Body>
      </Card>
      <Footer />
    </Page>
  );
};

export default UpgradeSuccess;
